import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { authService, type AuthResult } from '@app/services/auth.service';
import { useAuthStore } from '@app/store/auth.store';
import { useUiStore } from '@app/store/ui.store';

export type AuthStep = 'phone' | 'code' | 'success' | 'blocked';

export interface UsePhoneAuthOptions {
  /** Колбэк после успешной аутентификации (после записи токенов). */
  onSuccess?: () => void;
}

/**
 * usePhoneAuth — общая логика Phone+SMS flow.
 *
 * Используется:
 *   - AuthModal (открывается из Navbar)
 *   - AuthLayout (/app/auth — fallback для direct URL и ProtectedRoute)
 *
 * Шаги: phone → code → (success | blocked).
 * Status из Authenticate mutation определяет ветку:
 *   NEW          → success (заглушка, регистрация в Phase 1.5)
 *   FULL_ACCESS  → success + onSuccess()
 *   BLOCKED      → blocked
 */
export function usePhoneAuth(opts: UsePhoneAuthOptions = {}) {
  const navigate = useNavigate();
  const signIn = useAuthStore((s) => s.signIn);
  const closeAuthModal = useUiStore((s) => s.closeAuthModal);

  const [step, setStep] = useState<AuthStep>('phone');
  const [phone, setPhone] = useState('+7');
  const [code, setCode] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = useCallback(() => {
    setStep('phone');
    setPhone('+7');
    setCode(['', '', '', '']);
    setError(null);
    setLoading(false);
  }, []);

  const sendCode = useCallback(async (rawPhone: string) => {
    setError(null);
    const normalized = normalizePhone(rawPhone);
    if (!normalized) {
      setError('Введите номер телефона в формате +7 999 123-45-67');
      return false;
    }
    setLoading(true);
    try {
      await authService.sendCode(normalized);
      setPhone(normalized);
      setStep('code');
      return true;
    } catch (err) {
      setError(humanizeError(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Общий хвост любого способа входа: записать токены, развести по статусу,
   * отдать навигацию колбэку или редиректу по умолчанию.
   */
  const completeSignIn = useCallback(
    async (result: AuthResult) => {
      signIn({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        status: result.status,
      });

      if (result.status === 'BLOCKED') {
        setStep('blocked');
        return false;
      }

      setStep('success');
      // Небольшая пауза для UX (показать "вы вошли")
      await new Promise((r) => setTimeout(r, 600));

      // Новый пользователь идёт в визард регистрации — минуя onSuccess,
      // иначе модалка закрылась бы и он попал бы в закрытый раздел.
      if (result.status === 'NEW') {
        closeAuthModal();
        navigate('/app/registration', { replace: true });
        return true;
      }

      // Если onSuccess передан — делегируем навигацию колбэку,
      // иначе используем дефолтный редирект.
      if (opts.onSuccess) {
        opts.onSuccess();
      } else {
        closeAuthModal();
        navigate('/app/feed', { replace: true });
      }
      return true;
    },
    [signIn, opts, closeAuthModal, navigate]
  );

  const verifyCode = useCallback(
    async (rawCode: string) => {
      setError(null);
      if (rawCode.length !== 4) {
        setError('Введите 4 цифры из SMS');
        return false;
      }
      setLoading(true);
      try {
        const result = await authService.authenticate(phone, Number(rawCode));
        return await completeSignIn(result);
      } catch (err) {
        setError(humanizeError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [phone, completeSignIn]
  );

  /** Вход через Apple/Google. Отмена popup-а не считается ошибкой формы. */
  const signInWithProvider = useCallback(
    async (provider: 'apple' | 'google') => {
      setError(null);
      setLoading(true);
      try {
        const result =
          provider === 'apple'
            ? await authService.authenticateWithApple()
            : await authService.authenticateWithGoogle();
        return await completeSignIn(result);
      } catch (err) {
        if (!isUserCancelled(err)) setError(humanizeError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [completeSignIn]
  );

  const goBackToPhone = useCallback(() => {
    setStep('phone');
    setCode(['', '', '', '']);
    setError(null);
  }, []);

  return {
    step,
    phone,
    code,
    error,
    loading,
    setPhone,
    setCode,
    sendCode,
    verifyCode,
    signInWithProvider,
    goBackToPhone,
    reset,
  };
}

/** Закрытие popup-а пользователем — не ошибка, сообщение показывать не нужно. */
function isUserCancelled(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return (
    message.includes('popup_closed') ||
    message.includes('закрыто') ||
    message.includes('user_cancelled') ||
    message.includes('AbortError')
  );
}

/** Нормализация телефона: минимум 11 цифр, ведущий +. */
function normalizePhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  if (digits.length < 11) return null;
  return '+' + digits;
}

/** Преобразование ошибок в человекочитаемое сообщение. */
function humanizeError(err: unknown): string {
  if (!err) return 'Неизвестная ошибка';
  if (err instanceof Error && err.message) {
    if (
      err.message.includes('Failed to fetch') ||
      err.message.includes('NetworkError')
    ) {
      return 'Не удалось соединиться с сервером. Проверьте подключение к интернету.';
    }
    return err.message;
  }
  if (Array.isArray(err)) {
    return err
      .map((e) => e?.message)
      .filter(Boolean)
      .join('; ');
  }
  return 'Произошла ошибка. Попробуйте ещё раз.';
}
